@let theme = theme$ | async;
@let history = historyMap$ | async;
@let context = context$ | async;
@let isFetching = isFetching$ | async;
@let focusOnInput = focusOnInput$ | async;
@let sessionId = sessionId$ | async;
@let canAddAttachment = canAddAttachment$ | async;

<section class="flex h-full flex-col gap-y-24">
    <header
        class="relative flex w-full shrink-0 gap-x-8"
        [ngClass]="{
            'items-center justify-center': threadId === MAIN_THREAD,
            'items-start': threadId !== MAIN_THREAD,
        }"
    >
        @if (threadId === MAIN_THREAD) {
            <h2 class="sr-only">
                {{ 'AI.SECTION_TITLE' | translate: { AIstring: 'AI' } }}
            </h2>
            <interacta-image
                [alt]="''"
                [classes]="'h-40'"
                [src]="
                    theme === 'dark'
                        ? './assets/shared/images/ai/ai-interacta-header_D.svg'
                        : './assets/shared/images/ai/ai-interacta-header.svg'
                "
            />
        } @else {
            <button
                interacta-icon-button
                type="button"
                [icon]="'arrow-left'"
                [label]="'BUTTON.LABEL_BUTTON_BACK' | translate"
                [size]="'regular'"
                [style]="'text'"
                (click)="goToThread()"
            ></button>
            <div>
                <h2 class="typo-t5">Thread</h2>
                @if (threadName) {
                    <p class="typo-sm text-text-mid-contrast">
                        {{ threadName }}
                    </p>
                }
            </div>
        }
    </header>
    <div class="flex h-full min-h-0 grow overflow-hidden">
        @if (history | apply: objectIsEmpty) {
            <div class="flex w-full flex-col gap-y-24">
                <h3 class="self-center typo-t5">
                    {{ 'AI.SECTION_SUBTITLE' | translate }}
                </h3>
                <div class="flex flex-col gap-y-12 overflow-y-auto">
                    @if (context?.contextType === AIContextType.POST) {
                        <interacta-ai-panel-tip
                            [type]="AIPanelTipType.HAVE_A_RESUME"
                            (tipButtonClicked)="
                                getResume('AI.INFO_ABOUT_POST_SUMMARY_REQUEST')
                            "
                        />
                    }
                    @if (
                        context?.workflowEnabled &&
                        context?.contextType === AIContextType.POST
                    ) {
                        <interacta-ai-panel-tip
                            [type]="AIPanelTipType.QUESTIONS_ABOUT_WORKFLOWS"
                            (tipButtonClicked)="
                                startNewConversation(
                                    'AI.INFO_ABOUT_WORKFLOWS_REQUEST',
                                    'AI.INFO_ABOUT_WORKFLOWS_RESPONSE'
                                )
                            "
                        />
                    }
                    @if (context?.contextType === AIContextType.POST) {
                        <interacta-ai-panel-tip
                            [type]="AIPanelTipType.ASK_ME_A_QUESTION"
                            (tipButtonClicked)="
                                startNewConversation(
                                    'AI.NEW_CONVERSATION_REQUEST',
                                    'AI.NEW_CONVERSATION_RESPONSE'
                                )
                            "
                        />
                    }
                    @if (context?.contextType === AIContextType.GUIDE) {
                        <interacta-ai-panel-tip
                            [type]="AIPanelTipType.GUIDE_INTERACTA"
                            (tipButtonClicked)="
                                startNewConversation(
                                    'AI.INFO_ABOUT_INTERACTA_REQUEST',
                                    'AI.INFO_ABOUT_INTERACTA_RESPONSE'
                                )
                            "
                        />
                    }
                </div>
            </div>
        } @else {
            @if (history && (history | apply: getHistoryArray); as messages) {
                <div
                    #scrollContainer
                    class="flex min-h-0 grow flex-col gap-y-8 overflow-y-auto scroll-smooth"
                >
                    @for (message of messages; track message.id) {
                        @if (message.value && theme) {
                            <interacta-ai-message
                                class="w-full shrink-0"
                                [contextType]="
                                    context?.contextType ?? AIContextType.POST
                                "
                                [item]="message.value"
                                [last]="$last"
                                [messageKey]="message.id"
                                [theme]="theme"
                                [threadId]="threadId"
                                (goToThread)="goToThread($event)"
                                (openAttachmentPreview)="
                                    openAttachmentPreview($event)
                                "
                                (retry)="
                                    retry(messages[$index - 1].value.message)
                                "
                                (scrollToBottom)="scrollToBottom()"
                            />
                        }
                    }
                    @if (isFetching) {
                        <div
                            class="flex h-100 min-h-0 w-full shrink-0 gap-x-8 rounded-tl-2xl rounded-r-2xl bg-surface-A px-12 py-8 typo-sm shadow-chat"
                        >
                            <interacta-ai-loader
                                class="shrink-0"
                                [isDark]="(theme$ | async) === 'dark'"
                            />
                            <interacta-ai-skeleton-text
                                [lines]="loadingSkeletonLines"
                            />
                        </div>
                    }
                </div>
            }
        }
    </div>
    <div class="flex shrink-0 items-center gap-x-8">
        @if (canAddAttachment && context && context.contextId != null) {
            <interacta-ai-panel-chat-attachment-button
                [postId]="context.contextId"
                [theme]="theme ?? 'light'"
                [workflowEnabled]="context.workflowEnabled ?? false"
            />
        }
        <input
            #input
            autocomplete="off"
            class="grow rounded-3xl bg-surface-A p-12 typo-sm transition-shadow duration-1000 focus-visible:focus-visible-base"
            type="text"
            [formControl]="questionControl"
            [ngClass]="{
                'shadow-primary': focusOnInput,
                'shadow-none': !focusOnInput,
            }"
            [placeholder]="'AI.INPUT_PLACEHOLDER' | translate"
            (keyup.enter)="!isFetching && sendRequestToAI(sessionId)"
        />
        @if (questionControl.value.length > 0 && !isFetching) {
            <button
                interacta-icon-button
                type="submit"
                [icon]="'paper-plane'"
                [label]="'AI.SEND_MESSAGE' | translate"
                [size]="'large'"
                [style]="'text'"
                (click)="sendRequestToAI(sessionId)"
            ></button>
        }
    </div>
</section>
